import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Grid, useMediaQuery, useTheme } from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import { checkIfSettingPage, getVisitedMarketsFromLocalStorage, isMarketSearchAllowed } from 'src/utils/functions';
import {
  getUserInfo,
  setSelectedListLoadingAction,
  initializeSavedMarketsData,
} from 'src/store/Dashboard/actions';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { SubscriptionModalOrigin, SubscriptionPlan } from 'src/interfaces/idashboard';
import SavedMarketListPage from 'src/App/Dashboards/Market/Components/Filters/SavedMarketListPage/SavedMarketListPage';
import { useCanAccessExpertPlan } from 'src/hooks/useFeatureAccess';
import { useHistory } from 'react-router-dom';

const OverviewLandingPage = ({
  SearchBox,
  handleMarketSidebarClick,
  dummyMarket,
  handleOnSampleClick,
  currency,
  resetCurrencyToDefault,
  isLoggedIn,
  dummyMarketID
}: {
  SearchBox: any;
  handleMarketSidebarClick: (event: string) => Promise<void>;
  dummyMarket: any;
  handleOnSampleClick: (
    reqSubscriptionPlan?: SubscriptionPlan,
    origin?: SubscriptionModalOrigin | null
  ) => void;
  currency: string;
  resetCurrencyToDefault: () => void;
  isLoggedIn: boolean;
  dummyMarketID: string;
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const user = useSelector(state => (state as any).dashboard.user);
  const dispatch = useDispatch();
  const history = useHistory();
  const onlyRenderSavedMarkets = checkIfSettingPage();

  useEffect(() => {
    logAmplitudeEvent('market_landing_page_visited');
    resetCurrencyToDefault();
  }, []);

  interface UserMarketCardType {
    district: string;
    country: string;
    city: string;
    isSubscribed: boolean;
    isDemo: boolean;
  }

  const [recentlySearchedMarkets, setRecentlySearchedMarkets] = useState<UserMarketCardType[]>([]);

  const [savedMarkets, setSavedMarkets] = useState<UserMarketCardType[]>([]);
  const [openSearchModal, setOpenSearchModal] = useState<boolean>(false);
  const [selectedMarket, setSelectedMarket] = useState<any>(null);

  const savedMarketsState = useSelector((state: any) => state.dashboard.savedMarkets);
  const {
    data: selectedList,
    loading: selectedListLoading,
    isError: selectedListError,
  } = savedMarketsState;

  const isFreeUser = !useCanAccessExpertPlan();
  const isListInitialized = useRef<boolean>(false);

  useEffect(() => {
    dispatch(getUserInfo());
  }, [getUserInfo]);

  useEffect(() => {
    if (isLoggedIn && user.email?.toLowerCase() !== 'trial' && !isListInitialized.current) {
      dispatch(initializeSavedMarketsData());
      isListInitialized.current = true;
    }
    if (!isLoggedIn) {
      dispatch(setSelectedListLoadingAction(false));
    }
  }, [user, isLoggedIn]);

  useEffect(() => {
    const subscribedMarkets =
      user.regions &&
      Array.isArray(user.regions) &&
      [...user.regions].reverse().map(market => ({
        district: market.region,
        country: market.country ? market.country?.toLowerCase() : '0',
        city: market.city ? market.city?.toLowerCase() : '0',
        isSubscribed: true,
        isDemo: market.region?.toLowerCase() === dummyMarket.district?.toLowerCase(),
        origin: 'dynamodb',
      }));
    const recentlyVisitedMarkets = getVisitedMarketsFromLocalStorage();

    const marketMap = new Map();

    recentlyVisitedMarkets &&
      Array.isArray(recentlyVisitedMarkets) &&
      recentlyVisitedMarkets.reverse().forEach(market => {
        marketMap.set(market.district?.toLowerCase(), {
          ...market,
          country: market.country ? market.country?.toLowerCase() : '0',
          city: market.city ? market.city?.toLowerCase() : '0',
          isSubscribed: false,
          isDemo: market.district?.toLowerCase() === dummyMarket.district?.toLowerCase(),
          origin: 'localstorage',
        });
      });

    subscribedMarkets.forEach(market => {
      !marketMap.has(market.district?.toLowerCase()) &&
        marketMap.set(market.district?.toLowerCase(), market);
    });

    const uniqueMarkets = Array.from(marketMap.values());
    setRecentlySearchedMarkets(uniqueMarkets);

    const savedMarkets =
      user.savedMarkets &&
      [...user.savedMarkets].reverse().map(market => ({
        id: market.id,
        district: market.district.toLowerCase(),
        country: '0',
        city: market.city ? market.city?.toLowerCase() : '0',
        isSubscribed: true,
        isDemo: false,
      }));
    setSavedMarkets(savedMarkets);
  }, [user]);

  const handleCustomSearch = (event, value) => {
    const isSearchAllowed = isMarketSearchAllowed(user, value.id, dummyMarket.id);
    if (!isSearchAllowed) {
      handleOnSampleClick('expert');
      return;
    }

    // handle tailored region
    if (value.isDefaultOption) {
      history.push('/tailored/t');
      return;
    }
    setOpenSearchModal(true);
    setSelectedMarket(value);
  };

  return (
    <>
      {/* Keeping the default title and the description what was set as default */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          Market Research - Vacation / Short-term rental
        </title>
        <meta
          name="description"
          content={`Short-term and vacation rental data & analytics. `}
        />
      </Helmet>
      <Grid
        container
        item
        sx={{
          paddingBottom: matches ? '270px' : '70px',
          background: '#FFF',
          width: '100%',
        }}
        xs={12}
      >
        {!onlyRenderSavedMarkets && (
          <Grid
            container
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px',
              height: '50%',
            }}
          >
            <Grid container item justifyContent="center" xs={12}>
              <Grid
                item
                container
                sx={{
                  padding: '5px',
                  height: '90px',
                  maxWidth: '1600px',
                  borderRadius: '12px',
                  background: '#fff',
                  mt: 2,
                }}
                lg={8}
                md={8}
                xs={12}
              >
                {SearchBox('landing_page', false, handleCustomSearch)}
              </Grid>
            </Grid>
          </Grid>
        )}

        <Grid
          container
          item
          xs={12}
          sx={{
            width: '100%',
            backgroundColor: onlyRenderSavedMarkets ? '#fff' : '#E7F2F4',
            minHeight: '100px',
            borderTopLeftRadius: '40px',
          }}
        >
          <Grid
            container
            item
            xs
            sx={{
              background: '#fff',
              marginTop: onlyRenderSavedMarkets ? 0 : '20px',
              borderTopLeftRadius: '40px',
              padding: '20px',
            }}
            gap={4}
          >
            <Grid container item>
              <SavedMarketListPage
                isLoading={selectedListLoading}
                handleOnSampleClick={handleOnSampleClick}
                openSearchModal={openSearchModal}
                setOpenSearchModal={setOpenSearchModal}
                selectedMarket={selectedMarket}
                selectedList={selectedList}
                currency={currency}
                isLoggedIn={isLoggedIn}
                isMobileView={matches}
                dummyMarketID={dummyMarketID}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default OverviewLandingPage;
